/**
 * Purpose: Generic renderer for registry-resolved Business Detail modules
 *          (D-12). The page never knows about individual modules.
 * Inputs: modules (resolved descriptors), moduleCtx, moduleProps (shared
 *          callbacks/state passed to every module component).
 * Outputs: sectioned module list; each module lazy-loads (code-split) and
 *          mounts only when scrolled near the viewport (D-16), with a skeleton
 *          while loading.
 */
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { ModuleSkeleton } from './BusinessDetailSkeletons';

/** Defers children until the placeholder nears the viewport (below-the-fold budget). */
const LazyMount = ({ eager = false, children }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(eager);

  useEffect(() => {
    if (visible) return undefined;
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setVisible(true);
      },
      { rootMargin: '400px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  if (visible) return children;
  return (
    <div ref={ref}>
      <ModuleSkeleton />
    </div>
  );
};

const ModuleRenderer = ({ modules, moduleCtx, moduleProps = {} }) => {
  if (!modules?.length) return null;

  return (
    <div className="space-y-6">
      {modules.map((module, index) => (
        <section key={module.id} id={`module-${module.id}`} aria-label={module.title(moduleCtx)}>
          <LazyMount eager={index < 2}>
            <Suspense fallback={<ModuleSkeleton />}>
              <module.Component title={module.title(moduleCtx)} ctx={moduleCtx} {...moduleProps} />
            </Suspense>
          </LazyMount>
        </section>
      ))}
    </div>
  );
};

export default ModuleRenderer;
